/*
 * Copyright (C) 2025 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */

import { Component, input } from '@angular/core';
import { SvgIconComponent } from 'angular-svg-icon';
import { VariantProps, cva } from 'class-variance-authority';
import { IconName } from './icons';

export const iconVariants = cva('', {
  variants: {
    size: {
      full: 'size-full',
      small: 'size-4',
      'medium-small': 'size-5',
      medium: 'size-6',
      large: 'size-8',
      'extra-large': 'size-10',
      xxl: 'size-12',
    },
    fill: {
      primary: 'fill-primary',
      text: 'fill-text',
      whitetext: 'fill-whitetext',
      success: 'fill-success',
      'warning-dark': 'fill-warning-dark',
      pending: 'fill-pending',
      error: 'fill-error',
      pdf: 'fill-pdf',
      neutral: 'fill-neutral-500 dark:fill-neutral-400',
      'traffic-light-green': 'fill-traffic-light-green',
      'traffic-light-red': 'fill-traffic-light-red',
      destructive: 'fill-destructive',
      disabled: 'fill-disabled-button',
    },
    animation: {
      'rotate-0': 'transition-transform duration-100 ease-in-out',
      'rotate-90': 'transition-transform duration-100 ease-in-out rotate-90',
    },
  },
  defaultVariants: {
    size: 'medium',
    fill: 'primary',
  },
});

export type IconVariants = VariantProps<typeof iconVariants>;

export interface IconConfig {
  fill?: IconVariants['fill'];
  size?: IconVariants['size'];
  animation?: IconVariants['animation'];
  label?: string;
}

@Component({
  selector: 'ods-icon',
  imports: [SvgIconComponent],
  template: `<svg-icon
      [src]="'assets/icons/' + name() + '.svg'"
      [svgClass]="
        iconVariants({ size: config()?.size ?? 'medium', fill: config()?.fill ?? 'primary', animation: config()?.animation })
      "
      aria-hidden="true"
    />
    @if (config()?.label) {
      <span class="sr-only" data-test-id="sr-label">{{ config()?.label }}</span>
    }`,
  host: { class: 'block size-fit' },
})
export class IconComponent {
  public readonly name = input.required<IconName>();
  public readonly config = input<IconConfig>();

  public readonly iconVariants = iconVariants;
}
